import { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";

interface TimelineImage {
  src: string;
  alt: string;
}

interface TimelineImageGalleryProps {
  images: TimelineImage[];
}

const TimelineImageGallery = ({ images }: TimelineImageGalleryProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const openGallery = (index: number) => {
    setCurrentIndex(index);
    setIsOpen(true);
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowLeft") goToNext(); // RTL
    if (e.key === "ArrowRight") goToPrevious(); // RTL
  };

  return (
    <>
      {/* 2x2 Image Grid */}
      <div className="grid grid-cols-2 gap-2 sm:gap-3 md:gap-4">
        {images.map((img, idx) => (
          <button
            key={idx}
            onClick={() => openGallery(idx)}
            className="group relative overflow-hidden rounded-lg cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            <img
              src={img.src}
              alt={img.alt}
              loading="lazy"
              className="rounded-lg object-cover h-20 sm:h-28 md:h-44 lg:h-60 w-full shadow-md border border-border/40 transition-transform duration-300 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
              <span className="opacity-0 group-hover:opacity-100 text-white text-xs sm:text-sm font-medium transition-opacity duration-300">
                לחץ להגדלה
              </span>
            </div>
          </button>
        ))}
      </div>

      {/* Gallery Modal */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent 
          className="max-w-4xl w-[95vw] h-[85vh] p-0 bg-black/95 border-none overflow-hidden"
          onKeyDown={handleKeyDown}
        >
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-4 left-4 z-50 text-white hover:bg-white/20"
            onClick={() => setIsOpen(false)}
          >
            <X className="h-6 w-6" />
          </Button>

          <div className="relative w-full h-full flex items-center justify-center px-16 py-12">
            {/* Previous Button */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 z-40 text-white hover:bg-white/20 h-12 w-12"
              onClick={goToPrevious}
            >
              <ChevronRight className="h-8 w-8" />
            </Button>

            {/* Image */}
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                className="w-full h-full flex items-center justify-center"
              >
                <img
                  src={images[currentIndex].src}
                  alt={images[currentIndex].alt}
                  className="max-w-full max-h-full w-auto h-auto object-contain rounded-lg"
                  style={{ maxHeight: 'calc(85vh - 120px)' }}
                />
              </motion.div>
            </AnimatePresence>

            {/* Next Button */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-4 z-40 text-white hover:bg-white/20 h-12 w-12"
              onClick={goToNext}
            >
              <ChevronLeft className="h-8 w-8" />
            </Button>

            {/* Dots Indicator */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
              {images.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  className={`w-2 h-2 rounded-full transition-all duration-200 ${
                    idx === currentIndex 
                      ? "bg-primary w-4" 
                      : "bg-white/50 hover:bg-white/80"
                  }`}
                />
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TimelineImageGallery;
